import { PopoverPlacement } from "@idui/react-popover";
import { EditLocationAlt } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  CourseFormFields,
  CourseMetadataKeys,
  distanceFormula,
  editFormDataAF,
  EditFormMode,
  getCourseDurationMin,
  ICourseDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  useDrawers,
  useTimezone,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { TabsBottomInfoItemStyled } from "../../../right";
import { HeaderLine2Styled } from "./course.header.styled";

export const CourseHeaderInfos: FC = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { entity, mode } = useCrudEditContext<ICourseDto>();
  const courseDto = useRecoilValue<Partial<ICourseDto> | undefined>(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const { watch } = useFormContext<ICourseDto>();
  const { openEditFormDrawer } = useDrawers();
  const driverName = courseDto?.driverName ?? NO_VALUE_DISPLAY;
  const supplierName = courseDto?.supplierName ?? NO_VALUE_DISPLAY;

  const isManualPolylineEditFromForm = watch(
    CourseFormFields.isManualPolylineEdit,
  );
  const isManualPolylineEditFromDto =
    courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit];
  const showManualPolylineIndicator =
    isManualPolylineEditFromDto === true ||
    isManualPolylineEditFromForm === true;

  const courseDuration = useMemo(() => {
    const durationMin = getCourseDurationMin(courseDto, timezone);

    if (!durationMin) {
      return NO_VALUE_DISPLAY;
    }

    return `${durationMin}  ${t("general.MIN")}`;
  }, [courseDto, t, timezone]);

  return (
    <HeaderLine2Styled>
      <TabsBottomInfoItemStyled>
        <label className="label">{t("course.formLabel.id")}</label>
        <label data-e2e="course-id" className="info">
          {courseDto?.id || t("selectListLabel.New")}
        </label>
      </TabsBottomInfoItemStyled>

      <TabsBottomInfoItemStyled>
        <label data-e2e="course-distance">
          {courseDto?.metadata?.totalDistance
            ? distanceFormula(courseDto.metadata.totalDistance) +
              " " +
              t(`general.KM`)
            : NO_VALUE_DISPLAY}
        </label>
      </TabsBottomInfoItemStyled>
      <TabsBottomInfoItemStyled className="teal">
        <label data-e2e="course-duration">{courseDuration}</label>
      </TabsBottomInfoItemStyled>
      <TabsBottomInfoItemStyled className="course-header-line2-supplier">
        <FramerTooltip
          placement={PopoverPlacement.bottom}
          content={supplierName}
        >
          <div className="item__content truncate-text">
            <label className="label">{t("supplier.single")}</label>
            {!courseDto?.supplierId && (
              <label data-e2e="course-supplier" className="info">
                {NO_VALUE_DISPLAY}
              </label>
            )}
            {!!courseDto?.supplierId && (
              <span
                data-e2e="course-supplier"
                className="info pointer"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openEditFormDrawer({
                    entity: "supplier",
                    id: courseDto.supplierId || 0,
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openEditFormDrawer({
                    entity: "supplier",
                    id: courseDto.supplierId || 0,
                  }),
                )}
              >
                {supplierName}
              </span>
            )}
          </div>
        </FramerTooltip>
      </TabsBottomInfoItemStyled>

      <TabsBottomInfoItemStyled className="course-header-line2-driver">
        <FramerTooltip placement={PopoverPlacement.bottom} content={driverName}>
          <div className="item__content truncate-text">
            <label className="label">{t("driver.single")}</label>
            {!courseDto?.driverId && (
              <label data-e2e="course-driver" className="info">
                {NO_VALUE_DISPLAY}
              </label>
            )}
            {!!courseDto?.driverId && (
              <span
                data-e2e="course-driver"
                className="info pointer"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openEditFormDrawer({
                    entity: "driver",
                    id: courseDto.driverId || 0,
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openEditFormDrawer({
                    entity: "driver",
                    id: courseDto.driverId || 0,
                  }),
                )}
              >
                {driverName}
              </span>
            )}
          </div>
        </FramerTooltip>
      </TabsBottomInfoItemStyled>

      {showManualPolylineIndicator && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={t("course.MANUAL_POLYLINE_INDICATOR_TOOLTIP")}
          >
            <div className="item__content">
              <EditLocationAlt
                sx={{ fontSize: "16px", color: greyMui._700 }}
                data-e2e="course-manual-polyline-indicator"
              />
            </div>
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}
    </HeaderLine2Styled>
  );
};
